import type {
  ApplicantVM,
  ApplicantConsentVM,
  ApplicantFormVM,
  ApplicantAddress,
} from "./applicant";
import type { LOSType } from "./types";
import type { RatePlanDataVM } from "./pricing";
import type { ProgramTier, ProgramTierRefi } from "@/types/evaluation";
import type { AuthorizedThirdPartyVM } from "@/types/authorized-third-party";
import type { ApplicationStatusStep } from "@/types/stepper";
import type { ApplicationDetail } from "./application-details";
import type { JumpToMenuLink } from "@/types/jumpto-menu";
import type { StudentVM, StudentFormVM } from "@/types/student";

export interface TabVM {
  name: string;
  identifier: string;
  permissionId?: string;
  permissionLevel: number;
  tabStatus?: string;
  isTabActive: boolean;
}

export interface AddressVM {
  id: number;
  addressId: number; // not on ApplicantAddress
  address1: string;
  address2: string;
  addressTypeId: number;
  addressTypeName: string;
  addressUnitTypeId?: number | null;
  addressUnitTypeName?: string | null;
  applicantId: number;
  city: string;
  stateId: number;
  stateName: string;
  postalCode: string;
  countryId: number;
  countryName: string;
}

export interface ApplicationDetailVM {
  applicationDetailsVM: ApplicationDetail;
  applicationRatePlans: ApplicationRatePlans[];
  applicationStatusStepper: ApplicationStatusStep[];
  anticipatedDisbursementDate: string;
  anticipatedDibursementDisplay: string;
  canEdit: boolean;
  coApplicant: ApplicantVM;
  created: string;
  esignEnvelopeCreatedDisplay: string;
  esignEnvelopeSignedDisplay: string;
  expiration: string;
  hasMultipleTermsAvailable: boolean;
  hasPrimaryApplicant: boolean;
  hasRepaymentBasedRates: boolean;
  hasCoApplicant: boolean;
  hasStudent: boolean;
  id: number;
  isLegalPacketSigned: boolean;
  isRefi: boolean;
  isPEL: boolean;
  isParentLOC: boolean;
  isProfessionalGrad: boolean;
  isProfessionalGradWithResidency: boolean;
  isLoanReady: boolean;
  jumpToMenuLinks: JumpToMenuLink[];
  lenderId: number;
  lenderName: string;
  loanId: number;
  loanProgramLimitDisplay: string;
  loanProgramLimit: number;
  numberOfFixedTermsAvailable: number;
  numberOfVariableTermsAvailable: number;
  pageIdentifier: string;
  primaryApplicant: ApplicantVM;
  productTypeId: number;
  programTypeId?: number;
  programTierRefi: ProgramTier[] | ProgramTierRefi[];
  referenceId: string;
  rightToCancelDisplay: string;
  rightToCancel: string;
  showMoveToLoanService: boolean;
  showMovedToLoanServiceBanner: boolean;
  showJumpToMenu: boolean;
  statusId: number;
  statusName: string;
  statusReasonId?: number | null;
  student?: StudentVM;
  submittedDisplay: string;
  submitted: Date;
  tabMembership: TabMembershipVM;
  tollFreeNumber: string;
}

export interface ApplicationSummary {
  academicTerm: string;
  applicationStatuses: LOSType[];
  disabledStatuses: boolean;
  everHadRightToCancel: boolean;
  graduationDate: string;
  hasInitialDraw: boolean;
  initialDrawAmount: string;
  isRateSet: boolean;
  isRightToCancel: boolean;
  lenderName: string;
  loanId?: number | null;
  loanAmountDisplay: string;
  loanAmount: number;
  maxApprovedLimitDisplay: string;
  maxApprovedLimit: number;
  primaryApplicantInitials: string;
  primaryApplicantFullName: string;
  rate: string;
  rateTypeName: string;
  schoolName: string;
  studentFullName: null | string;
  statusId: number;
  statusName: string;
}

export interface ApplicationComment {
  id: number;
  applicationId: number;
  userId?: string | null;
  comment?: string | null;
  lastChangedAt?: Date | null;
  userDisplayName?: string | null;
  initials?: string | null;
}

export interface MembershipVM {
  applicantId: number;
  isMember: boolean;
  memberId: string;
  membershipStatusId: number;
  membershipStatusName: string;
  isJointOnStudentAccount: boolean;
}

export interface TabMembershipVM {
  primaryApplicantMembership: MembershipVM;
  coApplicantMembership: MembershipVM;
}

export enum NationalIdNumberTypes {
  SSN = 1,
  ITIN = 2,
}

export enum ApplicationStatuses {
  NotSubmitted = 1,
  Evaluation = 2,
  SubmittedProcessing = 3,
  Complete = 4,
  Declined = 5,
  Cancelled = 6,
  SMRDeclined = 7,
  RightToCancel = 8,
  SchoolCertification = 9,
}

export enum JudgmentDenialTypes {
  Other = 36,
}

export class JudgmentDenialTypeIds {
  public static get Other(): number {
    return JudgmentDenialTypes.Other;
  }
}

export class ApplicationStatusIds {
  public static get NOT_SUBMITTED(): number {
    return ApplicationStatuses.NotSubmitted;
  }
  public static get EVALUATION(): number {
    return ApplicationStatuses.Evaluation;
  }
  public static get SUBMITTED_AND_PROCESSING(): number {
    return ApplicationStatuses.SubmittedProcessing;
  }
  public static get COMPLETE(): number {
    return ApplicationStatuses.Complete;
  }
  public static get DECLINED(): number {
    return ApplicationStatuses.Declined;
  }
  public static get CANCELLED(): number {
    return ApplicationStatuses.Cancelled;
  }
  public static get SMR_DECLINED(): number {
    return ApplicationStatuses.SMRDeclined;
  }
  public static get RIGHT_TO_CANCEL(): number {
    return ApplicationStatuses.RightToCancel;
  }
  public static get SCHOOL_CERTIFICATION(): number {
    return ApplicationStatuses.SchoolCertification;
  }
}

export interface SchoolVM {
  id?: number;
  programTypeId: number;
  schoolId: number;
	schoolDetailsId: number;
  schoolName: string;
  levelTypeId: number;
  levelTypeName: string;
  major: string;
  degreeProgram: string;
  highestDegreeLevel?: string;
  highestDegreeLevelTypeId?: number;
  displayMajorDegree: string;
  academicProgramId?: number | null;
  academicProgramName: string | null;
  academicTerm: string;
  academicTermId: number | null;
}

export interface AuthorizedPersonRequest {
  id: number;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  phoneNumberTypeId?: number;
  emailAddress?: string;
  dateOfBirth?: Date;
  sourceTypeId: number;
  authorizedByApplicantId: number;
  addedByUserId: string;
  address: {
    id: number;
    applicantId: number;
    addressTypeId: number;
    addressTypeName: string;
    addressUnitTypeId?: number | null;
    addressUnitTypeName?: string;
    address1: string;
    address2?: string | null;
    city: string;
    stateId: number;
    stateName: string;
    countryId: number;
    countryName?: string;
    postalCode?: string;
  };
}
interface NotificationVM {
  id: number;
  status: string;
  title: string;
  text: string;
}

export interface TabApplicationVM {
  programTypeId: number;
  school: SchoolVM;
  hasPrimaryApplicant: boolean;
  primaryApplicant: ApplicantFormVM;
  hasCoApplicant: boolean;
  coApplicant: ApplicantFormVM;
  hasAuthorizedThirdParty: boolean;
  hasStudent: boolean;
  student: StudentFormVM;
  authorizedThirdParty: AuthorizedThirdPartyVM[];
  notifications: NotificationVM[];
  initialDraw?: InitialDraw;
  initialDrawRequest?: InitialDrawRequest;
}

export interface Cancellation {
  applicantId: number;
  statusReasonId: number;
  cancellationReason: string;
}

export interface ApplicationRatePlans {
  variableRateVMs?: RatePlanDataVM; // Mark as optional to align with backend
  fixedRateVMs?: RatePlanDataVM; // Mark as optional to align with backend
  yearlyTerm?: number;
  pricingTierIds?: number[]; // Mark as optional to align with backend
}

export interface CalculateApproved {
  maxApprovedAmount: number;
  maxApprovedAmountDisplay: string;
  hasMaxApprovedAmount: boolean;
}

export interface InitialDraw {
  amount?: number;
  enrollmentTermId: number;
  applicationId?: number; // not required when creating a updating initial draw
  lastUpdatedAtUtc?: Date;
  schoolBranchDOEId: string;
  schoolDOEId: string;
  schoolDetailsId: number;
  userId: string;
}

export interface InitialDrawRequest {
  amount?: number | null;
  enrollmentTermId: number;
  schoolBranchDOEId: string;
  schoolDOEId: string;
  schoolDetailsId: number;
  userId: string;
}

export interface InitialDrawSection {
  amount?: number;
  applicationId: number;
  enrollmentTermId: number;
  lastUpdatedAtUtc: Date;
  schoolBranchDOEId: string;
  schoolDOEId: string;
  schoolDetailsId: number;
  userId: string;
}

export interface CommentRequest {
  id: number;
  applicationId: number;
  userId: string;
  userDisplayName: string;
  comment: string;
}

export interface CancellationRequest {
  applicantId: number;
  statusReasonId: number;
  cancellationReason: string;
  userName?: string;
  userId?: string;
}

export interface ApplicationCommentRequest {
  id: number;
  applicationId: number;
  comment?: string | null;
  userId?: string | null;
  userDisplayName?: string | null;
}

export interface CancelApplicationRequest {
  cancellationRequest?: CancellationRequest | null;
  commentRequest?: ApplicationCommentRequest | null;
}
